import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Users } from 'lucide-react';
import axios from 'axios';

const Castes = () => {
  const [castes, setCastes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCaste, setEditingCaste] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: 'General'
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  const categories = ['General', 'OBC', 'SC', 'ST', 'EWS'];

  useEffect(() => {
    fetchCastes();
  }, []);

  const fetchCastes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/castes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCastes(response.data);
    } catch (error) {
      console.error('Error fetching castes:', error);
      alert('Failed to fetch castes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingCaste) {
        await axios.put(`${API_URL}/api/master-settings/castes/${editingCaste.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Caste updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/master-settings/castes`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Caste created successfully!');
      }
      fetchCastes();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving caste:', error);
      alert('Failed to save caste. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this caste?')) {
      setLoading(true);
      try {
        await axios.delete(`${API_URL}/api/master-settings/castes/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Caste deleted successfully!');
        fetchCastes();
      } catch (error) {
        console.error('Error deleting caste:', error);
        alert('Failed to delete caste. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (caste) => {
    setEditingCaste(caste);
    setFormData({
      name: caste.name,
      category: caste.category
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCaste(null);
    setFormData({ name: '', category: 'General' });
  };

  const filteredCastes = castes.filter(caste =>
    caste.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    caste.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryColor = (category) => {
    const colors = {
      'General': 'bg-blue-100 text-blue-800',
      'OBC': 'bg-green-100 text-green-800',
      'SC': 'bg-purple-100 text-purple-800',
      'ST': 'bg-orange-100 text-orange-800',
      'EWS': 'bg-yellow-100 text-yellow-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const categoryStats = categories.map(cat => ({
    name: cat,
    count: castes.filter(c => c.category === cat).length
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Castes</h1>
          <p className="text-sm text-gray-500 mt-1">Manage caste and category information</p>
        </div>
        <button
          onClick={() => {
            setShowModal(true);
            setEditingCaste(null);
            setFormData({ name: '', category: 'General' });
          }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          disabled={loading}
        >
          <Plus className="h-4 w-4" />
          Add Caste
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {categoryStats.map((stat) => (
          <div key={stat.name} className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs text-gray-600">{stat.name}</p>
            <p className="text-xl font-bold text-gray-900">{stat.count}</p>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search castes by name or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Caste Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredCastes.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                    No castes found. Click "Add Caste" to create one.
                  </td>
                </tr>
              ) : (
                filteredCastes.map((caste) => (
                  <tr key={caste.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {caste.name}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 text-xs rounded-full ${getCategoryColor(caste.category)}`}>
                        {caste.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm space-x-2">
                      <button
                        onClick={() => handleEdit(caste)}
                        className="text-blue-600 hover:text-blue-900"
                        disabled={loading}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(caste.id)}
                        className="text-red-600 hover:text-red-900"
                        disabled={loading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingCaste ? 'Edit Caste' : 'Add Caste'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Caste Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter caste name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : editingCaste ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Castes;